import isEqual from 'fast-deep-equal';

import { TypedBroadcastChannel } from '#browser/broadcast';
import { LeaderElector } from '#browser/leader-election';
import { LRUCache } from '#cache/lru-cache';
import { SECOND } from '#constants/times';
import { parseJSON } from '#json/parse';
import { ifDefined } from '#misc/if-defined';
import { isDefined } from '#misc/is-defined.guard';
import { isNotDefined } from '#misc/is-not-defined.guard';

import type { SetOptions } from './types/set.options';
import type { StorageChannelMessage, StorageChannelMessageTransaction } from './types/storage-channel-message.type';
import type { StorageEngineOptions } from './types/storage-engine.options';
import type { StorageEntry } from './types/storage-entry.type';
import type { StorageValue } from './types/storage-value.type';
import type { Transaction } from './types/transaction.type';

/**
 * Provides a robust, namespace-aware storage engine for browser environments, supporting localStorage,
 * in-memory caching, TTL expiration, sliding expiration, leader election for sweeping expired entries,
 * and cross-tab synchronization via BroadcastChannel.
 *
 * Features:
 * - Namespace isolation for storage keys.
 * - Optional in-memory LRU cache for performance.
 * - TTL and sliding expiration for entries.
 * - Automatic sweeping of expired entries (leader-elected).
 * - Cross-tab synchronization of changes (set, remove, clear) via BroadcastChannel.
 * - Debounced writes to reduce storage churn.
 * - Transactional batch operations.
 * - Quota management and fallback to memory cache.
 *
 * Usage:
 * - Use `set` and `get` for storing and retrieving values.
 * - Use `remove` and `clearNamespace` for deletion.
 * - Use `subscribe` to listen for changes.
 * - Use `transaction` for batch operations.
 * - Call `destroy` to clean up resources.
 *
 * @template StorageValue The type of value stored.
 * @template StorageEntry The internal entry format.
 * @template StorageEngineOptions Configuration options for the engine.
 * @template SetOptions Options for individual set operations.
 * @template Transaction Transaction interface for batch operations.
 * @template StorageChannelMessage Channel message for storage changes.
 *
 * @remarks
 * - Handles quota exceeded errors gracefully.
 * - Leader election ensures only one instance sweeps expired entries.
 * - Sliding expiration requires a TTL to be set.
 * - All operations are namespace-aware if a namespace is provided.
 * - Cross-tab synchronization ensures consistency across multiple instances in different tabs/windows.
 * - Transactions allow for atomic batch operations with minimal cross-tab communication.
 * - Subscribers are notified of changes after processing, including those from other instances via BroadcastChannel.
 * - The engine can be instantiated via `StorageEngineFactory` to manage singleton instances per namespace/channel.
 * - Example usage:
 * ```typescript
 * const storageEngine = StorageEngineFactory.getInstance(window.localStorage, { namespace: 'myApp' });
 * storageEngine.set('key', 'value', { ttlInMilliseconds: 60000 });
 * const value = storageEngine.get('key');
 * storageEngine.remove('key');
 * storageEngine.clearNamespace();
 * storageEngine.subscribe(() => { console.log('Storage changed!'); });
 * storageEngine.transaction((tx) => {
 * tx.set('key1', 'value1');
 * tx.remove('key2');
 * });
 * storageEngine.destroy();
 * ```
 */
export class StorageEngine {
  private cache?: LRUCache<string, StorageEntry>;

  private channel: TypedBroadcastChannel<StorageChannelMessage>;

  private debounceMs: number;

  private defaultTtlInMilliseconds?: number;

  private deserializeValue: (raw: StorageValue) => StorageValue;

  private destroyed = false;

  private readonly instanceId = crypto.randomUUID();

  private leaderElector?: LeaderElector;

  private maxNamespaceSizeInBytes?: number;

  private memoryFallback = false;

  private namespace?: string;

  private pendingWrites = new Map<string, number>();

  private serializeValue: (value: StorageValue) => StorageValue;

  private subscribers = new Set<() => void>();

  private sweepIntervalInMilliseconds?: number;

  private sweepTimer?: number;

  constructor(
    private storage: Storage = window.localStorage,
    options?: StorageEngineOptions
  ) {
    const {
      channelName,
      debounceMs,
      defaultTtlInMilliseconds,
      deserializeValue,
      maxNamespaceSizeInBytes,
      memoryCacheSize,
      namespace,
      serializeValue,
      sweepIntervalInMilliseconds,
    } = options ?? { memoryCacheSize: 100 };

    this.debounceMs = debounceMs ?? 0;
    this.defaultTtlInMilliseconds = defaultTtlInMilliseconds;
    this.deserializeValue =
      deserializeValue ??
      ((raw: StorageValue) => {
        const result = parseJSON(String(raw));

        return result.isFailure() ? undefined : result.unwrap();
      });
    this.maxNamespaceSizeInBytes = maxNamespaceSizeInBytes;
    this.namespace = namespace;
    this.serializeValue = serializeValue ?? JSON.stringify;
    this.sweepIntervalInMilliseconds = sweepIntervalInMilliseconds ?? 10 * SECOND;

    const resolvedChannelName = channelName ?? 'browser-storage';

    if (isDefined(memoryCacheSize)) {
      this.cache = new LRUCache(memoryCacheSize);
    }

    this.channel = new TypedBroadcastChannel<StorageChannelMessage>(resolvedChannelName, (message) => {
      if (message.sourceId === this.instanceId) {
        return;
      }

      this.handleChannelMessage(message);
    });

    if (isDefined(this.sweepIntervalInMilliseconds)) {
      this.leaderElector = new LeaderElector({
        channelName: `${resolvedChannelName}:leader`,
        leaderKey: `__browser_storage_leader__:${this.namespace}:${resolvedChannelName}`,
        onBecomeLeader: () => {
          this.startSweep();
        },
        onLeaderResign: () => {
          this.stopSweep();
        },
      });
    }
  }

  /**
   * Indicates whether the storage engine instance has been destroyed.
   *
   * @returns `true` if the instance has been destroyed; otherwise, `false`.
   */
  get isDestroyed() {
    return this.destroyed;
  }

  /**
   * Clears all entries within the current namespace.
   * - Removes all items from the underlying storage engine that belong to the namespace.
   * - Clears corresponding entries from the in-memory cache if it exists.
   * Posts a message to the storage channel to notify other instances to clear their namespaces as well.
   * @remarks
   * This method ensures that all instances of the storage engine that share the same namespace will clear their
   * storage when this method is called. If the namespace is not defined, the method will not perform any action.
   * Example usage:
   * ```typescript
   * storageEngine.clearNamespace();
   * ```
   */
  clearNamespace() {
    this.clearStorageByNamespace();

    this.postMessage({
      namespace: this.namespace,
      type: 'clear-namespace',
    });
  }

  /**
   * Destroys the storage engine instance, cleaning up all resources.
   * - Cancels pending debounced writes and clears the sweep timer.
   * - Destroys the {@link LeaderElector}, which will broadcast a resign message if this instance is the leader.
   * - Closes the storage sync channel and clears all subscribers.
   * @remarks
   * This method should be called when the storage engine instance is no longer needed.
   * Example usage:
   * ```typescript
   * storageEngine.destroy();
   * ```
   */
  destroy() {
    this.destroyed = true;

    for (const timeout of this.pendingWrites.values()) {
      clearTimeout(timeout);
    }

    this.pendingWrites.clear();

    if (isDefined(this.sweepTimer)) {
      clearInterval(this.sweepTimer);
    }

    this.leaderElector?.destroy();
    this.channel.close();
    this.subscribers.clear();
  }

  /**
   * Retrieves the value associated with the specified key from storage.
   *
   * - Checks the in-memory cache first; if present and not expired, returns the cached value.
   * - If the cached entry is expired, removes it and returns `undefined`.
   * - If not cached, attempts to fetch and parse the value from persistent storage.
   * - Removes and returns `undefined` if the stored entry is expired or parsing fails.
   * - Hydrates and caches the entry if valid, and refreshes sliding expiration if applicable.
   *
   * @param key The key to retrieve from storage.
   * @returns The value associated with the key, or `undefined` if not found or expired.
   */
  get(key: string): StorageValue {
    const fullKey = this.buildKey(key);
    const cached = this.cache?.get(fullKey);

    if (isDefined(cached)) {
      if (this.isExpired(cached)) {
        this.remove(key);

        return undefined;
      }

      this.refreshSlidingIfNeeded(key, fullKey, cached);

      return cached.value;
    }

    const raw = this.storage.getItem(fullKey);

    if (isNotDefined(raw)) {
      return undefined;
    }

    const parsed = parseJSON<StorageEntry>(raw);

    if (parsed.isFailure()) {
      this.storage.removeItem(fullKey);

      return undefined;
    }

    const entry = parsed.unwrap();

    if (this.isExpired(entry)) {
      this.remove(key);

      return undefined;
    }

    const value = this.deserializeValue(entry.value);

    const hydrated: StorageEntry = {
      ...entry,
      value,
    };

    this.cache?.set(fullKey, hydrated);

    this.refreshSlidingIfNeeded(key, fullKey, hydrated);

    return value;
  }

  /**
   * Removes the value associated with the specified key from storage and cache, and notifies other instances.
   * @param key The key to remove from storage.
   * @remarks
   * - Removes the item from the underlying storage engine and in-memory cache if it exists.
   * - Posts a message to the storage channel to notify other instances of the removal.
   * - The message includes the key, namespace, and type of operation ('remove').
   * - Subscribers are notified of the change after processing the removal.
   * - Example usage:
   * ```typescript
   * storageEngine.remove('key');
   * ```
   */
  remove(key: string) {
    this.removeInternal(key);

    this.postMessage({
      key,
      namespace: this.namespace,
      type: 'remove',
    });
  }

  /**
   * Sets a value in storage with optional settings for expiration and message posting.
   * @param key - The key to set the value for.
   * @param value - The value to store.
   * @param options - Optional settings for storing the value, including sliding expiration and TTL.
   * - If `options.postMessage` is true, a message will be posted to notify other instances of the change.
   * - If `options.sliding` is true, the expiration time will be extended each time the value is accessed.
   * - `options.ttlInMilliseconds` specifies the time-to-live for the value in milliseconds.
   * @remarks
   * - If the value is unchanged, the method returns early without performing any operations.
   * - If sliding expiration is enabled, a TTL must be provided; otherwise, an error is thrown.
   * - The method updates the in-memory cache and persistent storage, and posts a message if configured to do so.
   * - Example usage:
   * ```typescript
   * storageEngine.set('key', 'value', { sliding: true, ttlInMilliseconds: 60000 });
   * ```
   * */
  set(key: string, value: StorageValue, options?: SetOptions) {
    this.setInternal(key, value, true, options);
  }

  /**
   * Subscribes a listener function to be called whenever a change occurs in the storage engine.
   * The listener will be called after any set, remove, or clear operation is processed, including those from other
   * instances via the communication channel.
   * Returns an unsubscribe function that can be called to remove the listener from the subscription list.
   * @param listener - The function to be called when a storage change occurs.
   * @returns A function that can be called to unsubscribe the listener.
   * @remarks
   * - The listener will be called with no arguments whenever a change occurs in the storage engine.
   * - The method manages an internal set of subscribers and ensures that they are notified after processing any
   * storage change, including those received from other instances through the communication channel.
   * - Example usage:
   * ```typescript
   * const unsubscribe = storageEngine.subscribe(() => {
   *   console.log('Storage changed!');
   * });
   *
   * // To unsubscribe later:
   * unsubscribe();
   * ```
   */
  subscribe(listener: () => void) {
    this.subscribers.add(listener);

    return () => {
      return this.subscribers.delete(listener);
    };
  }

  /**
   * Performs a series of storage operations atomically within a transaction.
   * The provided callback receives a `Transaction` object with `set` and `remove` methods to queue operations.
   * After the callback executes, all queued operations are committed to storage and a single message is posted
   * to notify other instances.
   *
   * @param callback - A function that receives a `Transaction` object to perform multiple storage operations
   * atomically.
   * @remarks
   * - The `Transaction` object provides `set` and `remove` methods to queue operations.
   * - All operations performed within the callback are batched together and executed after the callback completes.
   * - A single message containing all operations is posted to the storage channel to minimize cross-tab
   * communication overhead.
   * - If `debounceMs` is configured, the commit of operations will be debounced accordingly.
   * - This method ensures that either all operations succeed together or none are applied, maintaining consistency
   * across tabs.
   * - Example usage:
   * ```typescript
   * storageEngine.transaction((tx) => {
   *  tx.set('key1', 'value1');
   *  tx.remove('key2');
   * });
   * ```
   */
  transaction(callback: (tx: Transaction) => void) {
    const operations: StorageChannelMessageTransaction['operations'] = [];

    const tx: Transaction = {
      remove: (key) => {
        operations.push({
          key,
          type: 'remove',
        });
      },
      set: (key, value, options) => {
        const { sliding, ttlInMilliseconds } = options ?? {};

        operations.push({
          key,
          sliding,
          ttlInMilliseconds,
          type: 'set',
          value,
        });
      },
    };

    callback(tx);

    for (const operation of operations) {
      const { key } = operation;

      if (operation.type === 'set') {
        const { sliding, ttlInMilliseconds, value } = operation;

        this.setInternal(key, value, false, {
          sliding,
          ttlInMilliseconds,
        });
      } else {
        this.removeInternal(key);
      }
    }

    this.postMessage({
      namespace: this.namespace,
      operations,
      sourceId: this.instanceId,
      type: 'transaction',
    });

    this.notify();
  }

  /**
   * Constructs a storage key by optionally prefixing it with the current namespace.
   *
   * If a namespace is defined, the resulting key will be in the format `${namespace}:${key}`.
   * Otherwise, the original key is returned unchanged.
   *
   * @param key - The base key to be used for storage.
   * @returns The namespaced key if a namespace exists, or the original key.
   */
  private buildKey(key: string) {
    return (
      ifDefined(this.namespace, (ns) => {
        return `${ns}:${key}`;
      }) ?? key
    );
  }

  /**
   * Calculates the total size (in characters) of all key-value pairs stored in the current namespace.
   *
   * Iterates through all keys in the storage, filters by the specified namespace (if defined),
   * and sums the lengths of each key and its corresponding value.
   *
   * @returns The total size of all key-value pairs in the namespace.
   */
  private calculateNamespaceSize(): number {
    let total = 0;

    for (const key of Object.keys(this.storage)) {
      if (isDefined(this.namespace) && !key.startsWith(this.namespace)) {
        continue;
      }

      const value = this.storage.getItem(key);

      if (isDefined(value)) {
        total += key.length + value.length;
      }
    }

    return total;
  }

  /**
   * Clears all storage items and cache entries that belong to the current namespace.
   *
   * Iterates through all keys in the storage and removes those that start with the namespace,
   * ensuring that only items associated with the namespace are deleted. Also removes corresponding
   * entries from the cache if it exists.
   *
   * If the namespace is not defined, the method exits without performing any action.
   */
  private clearStorageByNamespace() {
    if (isNotDefined(this.namespace)) {
      return;
    }

    for (const storageKey of Object.keys(this.storage)) {
      if (isDefined(this.namespace) && storageKey.startsWith(this.namespace)) {
        this.storage.removeItem(storageKey);
        this.cache?.delete(storageKey);
      }
    }
  }

  /**
   * Commits a value to storage under the specified key.
   *
   * Serializes the entry and attempts to store it in the underlying storage engine.
   * If storage quota is exceeded, handles the quota error and falls back to in-memory cache.
   * Updates the in-memory cache with the value and entry metadata.
   * Posts a message to notify other contexts of the change.
   *
   * @param fullKey - The fully qualified storage key.
   * @param value - The value to store.
   * @param entry - The storage entry metadata, including expiration and TTL.
   * @param postMessage - Whether to post a message to notify other contexts of the change.
   */
  private commit(fullKey: string, value: StorageValue, entry: StorageEntry, postMessage: boolean) {
    const raw = JSON.stringify(entry);

    if (this.memoryFallback) {
      this.cache?.set(fullKey, {
        expiresAt: entry.expiresAt,
        sliding: entry.sliding,
        ttlInMilliseconds: entry.ttlInMilliseconds,
        value,
      });

      return;
    }

    try {
      this.storage.setItem(fullKey, raw);
    } catch (error) {
      if (this.isQuotaExceeded(error)) {
        this.handleQuotaExceeded();

        try {
          this.storage.setItem(fullKey, raw);
        } catch {
          this.memoryFallback = true;
        }
      } else {
        throw error;
      }
    }

    this.cache?.set(fullKey, {
      expiresAt: entry.expiresAt,
      sliding: entry.sliding,
      ttlInMilliseconds: entry.ttlInMilliseconds,
      value,
    });

    if (!postMessage) {
      return;
    }

    this.postMessage({
      key: fullKey.replace(`${this.namespace}:`, ''),
      namespace: this.namespace,
      sliding: entry.sliding,
      ttlInMilliseconds: entry.ttlInMilliseconds,
      type: 'set',
      value,
    });
  }

  /**
   * Evicts the oldest entries from the storage to ensure the namespace size does not exceed the maximum allowed.
   *
   * Iterates through all storage entries within the current namespace, collects their expiration timestamps,
   * sorts them by expiration time (oldest first), and removes entries one by one until the namespace size
   * is within the configured limit (`maxNamespaceSizeInBytes`). Also removes corresponding entries from the cache.
   *
   * @private
   */
  private evictOldestEntries() {
    const entries: Array<{ expiresAt?: number; key: string }> = [];

    for (const key of Object.keys(this.storage)) {
      if (this.namespace && !key.startsWith(this.namespace)) {
        continue;
      }

      const raw = this.storage.getItem(key);

      if (isNotDefined(raw)) {
        continue;
      }

      const parsed = parseJSON<StorageEntry>(raw);

      if (parsed.isFailure()) {
        continue;
      }

      const entry = parsed.unwrap();

      entries.push({
        expiresAt: entry.expiresAt,
        key,
      });
    }

    entries.sort((a, b) => {
      return (a.expiresAt ?? 0) - (b.expiresAt ?? 0);
    });

    for (const entry of entries) {
      this.storage.removeItem(entry.key);
      this.cache?.delete(entry.key);

      if (isDefined(this.maxNamespaceSizeInBytes) && this.calculateNamespaceSize() <= this.maxNamespaceSizeInBytes) {
        break;
      }
    }
  }

  /**
   * Handles incoming messages from the storage channel and performs actions based on the message type.
   *
   * Supported message types:
   * - `'clear-namespace'`: Clears all storage entries for the current namespace.
   * - `'remove'`: Removes a specific item from storage and cache if the namespace matches.
   * - `'set'`: Sets or updates a storage entry and cache for the specified key and namespace,
   * including TTL and sliding expiration.
   *
   * After processing the message, notifies listeners of storage changes.
   *
   * @param message The {@link StorageChannelMessage} containing the action to perform and relevant data.
   */
  private handleChannelMessage(message: StorageChannelMessage) {
    switch (message.type) {
      case 'clear-namespace': {
        if (message.namespace === this.namespace) {
          this.clearStorageByNamespace();
        }
        break;
      }

      case 'remove': {
        if (message.namespace !== this.namespace) {
          return;
        }

        const fullKey = this.buildKey(message.key);

        this.storage.removeItem(fullKey);
        this.cache?.delete(fullKey);
        break;
      }

      case 'set': {
        if (message.namespace !== this.namespace) {
          return;
        }

        const fullKey = this.buildKey(message.key);
        const expiresAt = ifDefined(message.ttlInMilliseconds, (ttl) => {
          return Date.now() + ttl;
        });

        const entry: StorageEntry = {
          expiresAt,
          sliding: message.sliding,
          ttlInMilliseconds: message.ttlInMilliseconds,
          value: this.serializeValue(message.value),
        };

        this.storage.setItem(fullKey, JSON.stringify(entry));

        this.cache?.set(fullKey, {
          expiresAt,
          sliding: message.sliding,
          ttlInMilliseconds: message.ttlInMilliseconds,
          value: message.value,
        });

        break;
      }

      case 'transaction': {
        for (const operation of message.operations) {
          const { key, type } = operation;

          if (type === 'set') {
            const { sliding, ttlInMilliseconds, value } = operation;

            this.setInternal(key, value, false, {
              sliding,
              ttlInMilliseconds,
            });
          } else {
            this.removeInternal(key);
          }
        }
      }
    }

    this.notify();
  }

  /**
   * Handles the scenario when storage quota is exceeded.
   * If the current instance is the leader, it triggers eviction of the oldest entries
   * to free up space in the storage.
   *
   * @private
   */
  private handleQuotaExceeded() {
    if (this.leaderElector?.isLeader) {
      this.evictOldestEntries();
    }
  }

  /**
   * Determines whether a storage entry has expired based on its expiration timestamp.
   *
   * @param entry - The storage entry to check for expiration.
   * @returns `true` if the entry has an `expiresAt` value and the current time is past that value; otherwise, `false`.
   */
  private isExpired({ expiresAt }: StorageEntry) {
    return isDefined(expiresAt) && Date.now() > expiresAt;
  }

  /**
   * Determines whether the provided error indicates that the browser's storage quota has been exceeded.
   *
   * @param error - The error object to check, typically thrown during storage operations.
   * @returns `true` if the error is a `DOMException` with a name indicating quota exceeded; otherwise, `false`.
   */
  private isQuotaExceeded(error: unknown) {
    return (
      error instanceof DOMException &&
      (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
    );
  }

  /**
   * Notifies all registered subscribers by invoking their callback functions.
   *
   * This method iterates through the list of subscribers and calls each one,
   * typically used to signal that a change has occurred in the storage engine.
   *
   * @private
   */
  private notify() {
    for (const sub of this.subscribers) {
      sub();
    }
  }

  /**
   * Sends a message to the storage channel, attaching the current instance's source ID.
   * After posting the message, triggers notification logic.
   *
   * @param message - The message to be sent through the storage channel.
   */
  private postMessage(message: StorageChannelMessage) {
    if (this.destroyed) {
      return;
    }

    this.channel.post({
      ...message,
      sourceId: this.instanceId,
    });
    this.notify();
  }

  /**
   * Refreshes the expiration time of a sliding cache entry if applicable.
   *
   * If the entry is configured with sliding expiration and a valid TTL,
   * this method updates the `expiresAt` timestamp, serializes the value,
   * and persists the refreshed entry in both storage and cache. It also
   * broadcasts a message to notify other consumers of the update.
   *
   * @param key - The logical key for the entry.
   * @param fullKey - The fully qualified key used in storage.
   * @param entry - The storage entry to refresh.
   */
  private refreshSlidingIfNeeded(key: string, fullKey: string, entry: StorageEntry) {
    const { sliding, ttlInMilliseconds, value } = entry;

    if (!sliding || isNotDefined(ttlInMilliseconds)) {
      return;
    }

    const newExpiresAt = Date.now() + ttlInMilliseconds;

    const refreshed: StorageEntry = {
      ...entry,
      expiresAt: newExpiresAt,
      value: this.serializeValue(value),
    };

    this.storage.setItem(fullKey, JSON.stringify(refreshed));

    this.cache?.set(fullKey, {
      ...entry,
      expiresAt: newExpiresAt,
    });

    this.postMessage({
      key,
      namespace: this.namespace,
      sliding: true,
      ttlInMilliseconds,
      type: 'set',
      value,
    });
  }

  /**
   * Removes the specified item from both the underlying storage and the optional in-memory cache.
   *
   * @param key - The key identifying the item to remove. The key will be processed by `buildKey`
   * to generate the full storage key.
   * @remarks
   * This method deletes the item from persistent storage (e.g., localStorage or sessionStorage) and,
   * if a cache is present, also removes it from the cache.
   */
  private removeInternal(key: string) {
    const fullKey = this.buildKey(key);

    this.storage.removeItem(fullKey);
    this.cache?.delete(fullKey);
  }

  /**
   * Sets a value in the storage engine with optional sliding expiration and debouncing.
   *
   * @param key - The key under which the value will be stored.
   * @param value - The value to store.
   * @param postMessage - Whether to broadcast a message after setting the value.
   * @param options - Optional settings for the storage entry, including sliding expiration and TTL.
   *   - `sliding`: If true, enables sliding expiration for the entry.
   *   - `ttlInMilliseconds`: Time-to-live in milliseconds for the entry.
   *
   * @throws {Error} If sliding expiration is enabled but no TTL is provided.
   *
   * @remarks
   * - If the value is unchanged, the method returns early.
   * - If debouncing is enabled, the write is delayed by the configured debounce interval.
   * - Otherwise, the value is committed to storage immediately.
   */
  private setInternal(key: string, value: StorageValue, postMessage: boolean, options?: SetOptions) {
    const fullKey = this.buildKey(key);
    const sliding = options?.sliding ?? false;
    const ttlInMilliseconds = options?.ttlInMilliseconds ?? this.defaultTtlInMilliseconds;

    if (sliding && isNotDefined(ttlInMilliseconds)) {
      throw new Error('Sliding expiration requires ttlInMilliseconds or a defaultTtlInMilliseconds.');
    }

    const expiresAt = ifDefined(ttlInMilliseconds, (milliseconds) => {
      return Date.now() + milliseconds;
    });

    const entry: StorageEntry = {
      expiresAt,
      sliding,
      ttlInMilliseconds: sliding ? ttlInMilliseconds : undefined,
      value: this.serializeValue(value),
    };

    const raw = JSON.stringify(entry);
    const current = this.storage.getItem(fullKey);

    if (isEqual(current, raw)) {
      return;
    }

    if (this.debounceMs > 0) {
      clearTimeout(this.pendingWrites.get(fullKey));

      const timeout = window.setTimeout(() => {
        this.commit(fullKey, value, entry, postMessage);
      }, this.debounceMs);

      this.pendingWrites.set(fullKey, timeout);
    } else {
      this.commit(fullKey, value, entry, postMessage);
    }
  }

  /**
   * Starts the periodic sweep process to remove expired entries from storage.
   * Called by the {@link LeaderElector} when this instance acquires leadership.
   * The sweep process runs at intervals defined by `sweepIntervalInMilliseconds`.
   *
   * @private
   */
  private startSweep() {
    if (isDefined(this.sweepTimer)) {
      return;
    }

    this.sweepTimer = window.setInterval(() => {
      if (this.leaderElector?.isLeader) {
        this.sweepExpiredEntries();
      }
    }, this.sweepIntervalInMilliseconds);
  }

  /**
   * Stops the periodic sweep process.
   * Called by the {@link LeaderElector} when this instance yields leadership.
   *
   * @private
   */
  private stopSweep() {
    if (isDefined(this.sweepTimer)) {
      clearInterval(this.sweepTimer);
      this.sweepTimer = undefined;
    }
  }

  /**
   * Removes expired entries from the storage and cache.
   *
   * Iterates through all storage keys, filtering by namespace if defined.
   * For each entry, parses the stored value and checks its expiration.
   * If the entry is expired, it is removed from storage and cache,
   * and a 'remove' message is posted.
   *
   * Additionally, if the current instance is the leader and the namespace size
   * exceeds the maximum allowed, triggers eviction of the oldest entries.
   *
   * @private
   */
  private sweepExpiredEntries() {
    const now = Date.now();
    const removedKeys: string[] = [];

    for (const storageKey of Object.keys(this.storage)) {
      if (isDefined(this.namespace) && !storageKey.startsWith(this.namespace)) {
        continue;
      }

      const raw = this.storage.getItem(storageKey);

      if (isNotDefined(raw)) {
        continue;
      }

      const parsed = parseJSON<StorageEntry>(raw);

      if (parsed.isFailure()) {
        this.storage.removeItem(storageKey);
        this.cache?.delete(storageKey);
        continue;
      }

      const entry = parsed.unwrap();

      if (isDefined(entry.expiresAt) && now > entry.expiresAt) {
        this.storage.removeItem(storageKey);
        this.cache?.delete(storageKey);

        const keyWithoutNamespace = isDefined(this.namespace)
          ? storageKey.replace(`${this.namespace}:`, '')
          : storageKey;

        removedKeys.push(keyWithoutNamespace);
      }
    }

    if (removedKeys.length > 0) {
      this.postMessage({
        namespace: this.namespace,
        operations: removedKeys.map((key) => {
          return { key, type: 'remove' };
        }),
        sourceId: this.instanceId,
        type: 'transaction',
      });
    }

    if (
      this.leaderElector?.isLeader &&
      isDefined(this.maxNamespaceSizeInBytes) &&
      this.calculateNamespaceSize() > this.maxNamespaceSizeInBytes
    ) {
      this.evictOldestEntries();
    }
  }
}
